// Configurações → Templates IW (só administrador): envia os HTMLs dos templates de evolução do IW.
// O servidor guarda cada versão e monta o dicionário dos campos (código → texto), que traduz as
// exportações do IW para preencher o Carefy. Dá para ver os campos e testar com uma planilha do IW.
import React, { useEffect, useState } from 'react';
import { Upload, FileText, RefreshCw } from 'lucide-react';
import { supabase } from '../supabase';

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', primary: '#4f46e5' };
const TIPO = { lista: 'lista', marcar: 'marcar', texto: 'texto', html: 'texto longo (HTML)' };
const fmt = iso => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

async function chamar(corpo) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch('/api/iw-templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
    body: JSON.stringify(corpo),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
  return j;
}
// HTML do IW às vezes vem em Latin-1: se o UTF-8 der caractere inválido, lê de novo
const lerArquivo = (f, latin1 = false) => new Promise((ok, erro) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result));
  r.onerror = () => erro(new Error(`Não foi possível ler ${f.name}`));
  r.readAsText(f, latin1 ? 'ISO-8859-1' : 'UTF-8');
});
const lerTexto = async (f, latin1) => { const t = await lerArquivo(f, latin1); return !latin1 && t.includes('�') ? lerArquivo(f, true) : t; };

export default function TemplatesIW() {
  const [lista, setLista] = useState([]);
  const [erro, setErro] = useState('');
  const [fila, setFila] = useState([]); // [{ nome, html, arquivo }]
  const [colado, setColado] = useState({ nome: '', html: '' });
  const [enviando, setEnviando] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [aberto, setAberto] = useState(null); // { chave, dic }
  const [teste, setTeste] = useState(null);

  const carregar = () => chamar({ acao: 'listar' }).then(j => setLista(j.templates || [])).catch(e => setErro(e.message));
  useEffect(() => { carregar(); }, []);

  const escolher = async files => {
    setErro('');
    const novos = [];
    for (const f of files) novos.push({ arquivo: f.name, nome: f.name.replace(/\.(html?|txt)$/i, '').replace(/[_]+/g, ' ').trim(), html: await lerTexto(f) });
    setFila(q => [...q, ...novos]);
  };
  const enviar = async itens => {
    setEnviando(true); setErro('');
    const out = [];
    for (const it of itens) {
      try { out.push({ nome: it.nome, ...(await chamar({ acao: 'enviar', nome: it.nome, html: it.html })) }); } catch (e) { out.push({ nome: it.nome, erro: e.message }); }
    }
    setResultados(out); setFila([]); setColado({ nome: '', html: '' });
    setEnviando(false); carregar();
  };
  const ver = async chave => {
    if (aberto?.chave === chave) { setAberto(null); return; }
    setTeste(null);
    try { setAberto({ chave, ...(await chamar({ acao: 'ver', chave })) }); } catch (e) { setErro(e.message); }
  };
  const testar = async (chave, f) => {
    setErro('');
    try { setTeste({ chave, ...(await chamar({ acao: 'traduzir', chave, csv: await lerTexto(f, true) })) }); } catch (e) { setErro(e.message); }
  };

  const caixa = { background: P.white, border: `1px solid ${P.border}`, borderRadius: 12, padding: 16 };
  const inp = { border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 9px', fontSize: '0.85rem', fontFamily: 'inherit' };
  const btn = (cor = P.primary) => ({ border: 0, background: cor, color: '#fff', borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' });

  return (
    <div className="tab-content" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h3 style={{ margin: 0, color: P.text }}>🧩 Templates de evolução do IW</h3>
        <div style={{ fontSize: '0.82rem', color: P.muted, marginTop: 4 }}>
          Envie o HTML de cada template (enfermagem, médico, fisioterapia…). O Quality guarda cada versão e monta o dicionário dos campos —
          o que traduz as exportações do IW ("PAD_COURO_CAB = 4" → "Sem alterações") para preencher o Carefy. Se o template mudar no IW, é só enviar de novo.
        </div>
      </div>
      {erro && <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px', fontSize: '0.85rem' }}>{erro}</div>}

      <div style={{ ...caixa, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>Enviar arquivos .html</div>
          <label style={{ ...btn(), display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <Upload size={15} /> Escolher arquivos
            <input type="file" accept=".html,.htm,.txt,text/html" multiple style={{ display: 'none' }} onChange={e => { escolher([...e.target.files]); e.target.value = ''; }} />
          </label>
          {fila.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {fila.map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.8rem' }}>
                  <FileText size={14} color={P.muted} />
                  <input value={f.nome} onChange={e => setFila(q => q.map((x, k) => (k === i ? { ...x, nome: e.target.value } : x)))} style={{ ...inp, flex: 1 }} aria-label="Nome do template" />
                  <span style={{ color: P.muted }}>{Math.round(f.html.length / 1024)} KB</span>
                  <button type="button" onClick={() => setFila(q => q.filter((_, k) => k !== i))} style={{ border: 0, background: 'none', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                </div>
              ))}
              <button type="button" disabled={enviando} onClick={() => enviar(fila)} style={{ ...btn('#16a34a'), alignSelf: 'flex-start' }}>{enviando ? 'Enviando…' : `Enviar ${fila.length} template(s)`}</button>
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>…ou colar o HTML</div>
          <input value={colado.nome} onChange={e => setColado(c => ({ ...c, nome: e.target.value }))} placeholder="Nome (ex.: Evolução de Enfermagem)" style={inp} />
          <textarea value={colado.html} onChange={e => setColado(c => ({ ...c, html: e.target.value }))} rows={4} placeholder="<html>…" style={{ ...inp, fontFamily: 'monospace', fontSize: '0.75rem' }} />
          <button type="button" disabled={enviando || !colado.nome.trim() || colado.html.length < 50} onClick={() => enviar([colado])} style={{ ...btn(), alignSelf: 'flex-start', opacity: !colado.nome.trim() || colado.html.length < 50 ? 0.5 : 1 }}>Enviar</button>
        </div>
      </div>

      {resultados.length > 0 && (
        <div style={caixa}>
          {resultados.map((r, i) => (
            <div key={i} style={{ fontSize: '0.82rem', marginBottom: 4 }}>
              {r.erro ? <span style={{ color: '#991b1b' }}>✕ {r.nome}: {r.erro}</span> : (
                <span style={{ color: '#166534' }}>
                  ✓ <strong>{r.nome}</strong> — {r.resumo.campos} campos ({r.resumo.listas} listas, {r.resumo.marcar} de marcar, {r.resumo.textos + r.resumo.html} de texto)
                  {r.igualAnterior ? ' · igual à versão anterior (não guardei de novo)' : r.novo ? ' · novo' : r.mudancas && ` · mudou: ${r.mudancas.novos.length} campo(s) novo(s), ${r.mudancas.removidos.length} removido(s), ${r.mudancas.opcoesAlteradas.length} com opções alteradas`}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ ...caixa, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: `1px solid ${P.border}` }}>
          <strong style={{ fontSize: '0.88rem' }}>Templates guardados ({lista.length})</strong>
          <button type="button" onClick={carregar} title="Atualizar" style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '4px 8px', cursor: 'pointer' }}><RefreshCw size={13} /></button>
        </div>
        {!lista.length && <div style={{ padding: 18, color: P.muted, fontSize: '0.85rem' }}>Nenhum template ainda.</div>}
        {lista.map(t => (
          <div key={t.chave} style={{ borderBottom: `1px solid #f1f5f9`, padding: '10px 14px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontWeight: 800, color: P.text }}>{t.nome}</div>
                <div style={{ fontSize: '0.75rem', color: P.muted }}>
                  {t.titulo && `${t.titulo} · `}{t.resumo?.campos} campos · {t.versoes} versão(ões) · última em {fmt(t.created_at)} por {t.por}
                </div>
              </div>
              <button type="button" onClick={() => ver(t.chave)} style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>{aberto?.chave === t.chave ? 'Fechar' : 'Ver campos'}</button>
              <label style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }} title="Escolha uma planilha (CSV) exportada do IW com este template">
                Testar com planilha do IW
                <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) testar(t.chave, e.target.files[0]); e.target.value = ''; }} />
              </label>
            </div>

            {teste?.chave === t.chave && (
              <div style={{ marginTop: 10, background: P.bg, borderRadius: 10, padding: 12, fontSize: '0.8rem' }}>
                <div style={{ color: P.muted, marginBottom: 6 }}>
                  {teste.linhas} evolução(ões) na planilha · {teste.semNoTemplate.length} coluna(s) da planilha sem campo no template{teste.semNoTemplate.length ? ` (${teste.semNoTemplate.slice(0, 12).join(', ')}${teste.semNoTemplate.length > 12 ? '…' : ''})` : ''}
                </div>
                {teste.evolucoes.slice(0, 1).map(ev => (
                  <div key={ev.evolucao}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Evolução {ev.evolucao} · atendimento {ev.atendimento} · {ev.data}</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {ev.campos.map((c, i) => (
                          <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '4px 6px', color: P.muted, width: '22%' }}>{c.secao}</td>
                            <td style={{ padding: '4px 6px', fontWeight: 700, width: '25%' }}>{c.rotulo}</td>
                            <td style={{ padding: '4px 6px', whiteSpace: 'pre-wrap' }}>{c.valor}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}

            {aberto?.chave === t.chave && (
              <div style={{ marginTop: 10, maxHeight: 420, overflow: 'auto', border: `1px solid ${P.border}`, borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                  <thead><tr>{['Seção', 'Campo (coluna)', 'Tipo', 'Rótulo', 'Opções'].map(h => <th key={h} style={{ position: 'sticky', top: 0, background: P.bg, textAlign: 'left', padding: '6px 8px', color: P.muted }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {aberto.dicionario.campos.map(c => (
                      <tr key={c.campo} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '4px 8px', color: P.muted }}>{c.secao}</td>
                        <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{c.campo}</td>
                        <td style={{ padding: '4px 8px' }}>{TIPO[c.tipo]}</td>
                        <td style={{ padding: '4px 8px', fontWeight: 600 }}>{c.tipo === 'marcar' ? `${c.grupo}: ${c.texto}` : c.rotulo}</td>
                        <td style={{ padding: '4px 8px', color: '#475569' }}>{c.opcoes ? Object.entries(c.opcoes).map(([k, v]) => `${k}=${v}`).join(' · ') : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
